import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Loader2, X, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { API } from "@/lib/site";

const empty = { title: "", excerpt: "", cover_image: "", content: "", published: true };

export default function BlogManager({ adminKey }) {
  const [posts, setPosts] = useState([]);
  const [editing, setEditing] = useState(null); // null | {} (new) | post
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const headers = { "x-admin-key": adminKey };

  const load = useCallback(() => {
    axios.get(`${API}/posts`, { headers }).then((r) => setPosts(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminKey]);

  useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm(empty); setEditing({}); };
  const openEdit = (p) => { setForm({ title: p.title, excerpt: p.excerpt, cover_image: p.cover_image, content: p.content, published: p.published }); setEditing(p); };
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error("Tajuk diperlukan"); return; }
    setSaving(true);
    try {
      if (editing.id) {
        await axios.put(`${API}/posts/${editing.id}`, form, { headers });
        toast.success("Artikel dikemas kini");
      } else {
        await axios.post(`${API}/posts`, form, { headers });
        toast.success("Artikel diterbitkan");
      }
      setEditing(null);
      load();
    } catch {
      toast.error("Gagal menyimpan artikel");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Padam artikel ini?")) return;
    try {
      await axios.delete(`${API}/posts/${id}`, { headers });
      toast.success("Artikel dipadam");
      load();
    } catch {
      toast.error("Gagal memadam");
    }
  };

  if (editing) {
    return (
      <div className="rounded-2xl bg-white border border-slate-200 p-7" data-testid="blog-editor">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-heading text-xl font-bold text-[#0A2540]">
            {editing.id ? "Edit Artikel" : "Artikel Baru"}
          </h2>
          <button onClick={() => setEditing(null)} className="text-slate-400 hover:text-slate-700" data-testid="blog-editor-close">
            <X className="h-5 w-5" />
          </button>
        </div>
        <form onSubmit={save} className="space-y-5">
          <div>
            <Label className="text-[#0A2540]">Tajuk *</Label>
            <Input value={form.title} onChange={(e) => update("title", e.target.value)} placeholder="cth: 5 Tips Lulus Pinjaman Rumah" className="mt-1.5" data-testid="blog-input-title" />
          </div>
          <div>
            <Label className="text-[#0A2540]">Ringkasan (excerpt)</Label>
            <Textarea value={form.excerpt} onChange={(e) => update("excerpt", e.target.value)} rows={2} placeholder="Ayat ringkas yang dipaparkan pada senarai blog" className="mt-1.5" data-testid="blog-input-excerpt" />
          </div>
          <div>
            <Label className="text-[#0A2540]">URL Gambar Muka (pilihan)</Label>
            <Input value={form.cover_image} onChange={(e) => update("cover_image", e.target.value)} placeholder="https://..." className="mt-1.5" data-testid="blog-input-cover" />
          </div>
          <div>
            <Label className="text-[#0A2540]">Kandungan (Markdown disokong) *</Label>
            <Textarea value={form.content} onChange={(e) => update("content", e.target.value)} rows={12} placeholder="Tulis kandungan artikel di sini. Gunakan ## untuk tajuk, - untuk senarai." className="mt-1.5 font-mono text-sm" data-testid="blog-input-content" />
          </div>
          <div className="flex items-center gap-3">
            <Switch checked={form.published} onCheckedChange={(v) => update("published", v)} data-testid="blog-input-published" />
            <span className="text-sm text-slate-600">{form.published ? "Diterbitkan" : "Draf (tidak dipaparkan)"}</span>
          </div>
          <div className="flex gap-3">
            <Button type="submit" disabled={saving} className="bg-[#0A2540] hover:bg-[#0d3358] text-white rounded-full" data-testid="blog-save-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Simpan Artikel"}
            </Button>
            <Button type="button" variant="outline" onClick={() => setEditing(null)} className="rounded-full">Batal</Button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div data-testid="blog-manager">
      <div className="flex items-center justify-between mb-5">
        <h2 className="font-heading text-lg font-bold text-[#0A2540]">Urus Artikel Blog</h2>
        <Button onClick={openNew} className="bg-[#0A2540] hover:bg-[#0d3358] text-white rounded-full" data-testid="blog-new-btn">
          <Plus className="mr-1.5 h-4 w-4" /> Artikel Baru
        </Button>
      </div>
      <div className="rounded-2xl bg-white border border-slate-200 overflow-hidden">
        {posts.length === 0 ? (
          <p className="py-16 text-center text-slate-500" data-testid="blog-manager-empty">Belum ada artikel. Klik &quot;Artikel Baru&quot; untuk mula.</p>
        ) : (
          <div className="divide-y divide-slate-100">
            {posts.map((p) => (
              <div key={p.id} className="flex items-center justify-between px-6 py-4" data-testid={`blog-row-${p.slug}`}>
                <div className="min-w-0">
                  <p className="font-semibold text-[#0A2540] truncate">{p.title}</p>
                  <p className="text-xs text-slate-500 truncate">/blog/{p.slug}</p>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <Badge className={p.published ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}>
                    {p.published ? "Terbit" : "Draf"}
                  </Badge>
                  <a href={`/blog/${p.slug}`} target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-[#1D4ED8]"><ExternalLink className="h-4 w-4" /></a>
                  <button onClick={() => openEdit(p)} className="text-slate-400 hover:text-[#0A2540]" data-testid={`blog-edit-${p.slug}`}><Pencil className="h-4 w-4" /></button>
                  <button onClick={() => remove(p.id)} className="text-slate-400 hover:text-red-500" data-testid={`blog-delete-${p.slug}`}><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
